import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ApiError,
  approveWorkflowArtifact,
  createCompanyWorkflowRequest,
  fetchCompany,
  fetchOperations,
  fetchWorkflowActionCatalog,
} from "../lib/api";
import { useCan } from "../lib/session";
import type { CompanyDetailResponse, WorkflowActionDefinition } from "../lib/types";

function ago(value: string | null): string {
  if (!value) return "-";
  const seconds = Math.max(0, (Date.now() - Date.parse(value)) / 1000);
  if (seconds < 60) return `${Math.round(seconds)}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  return `${Math.round(seconds / 3600)}h ago`;
}

function duration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}

function tone(status: string): string {
  if (["authenticated", "succeeded", "completed", "ok"].includes(status)) return "green";
  if (["failed", "broken", "unauthenticated"].includes(status)) return "red";
  if (["stale", "retry", "attention"].includes(status)) return "amber";
  return "blue";
}

// Mirror of the legacy company-workspace gate: an approved scope exists when
// any request carries approved_scoper_artifact_id or an approved
// approved_scope artifact exists.
function hasApprovedScope(rows: {
  workflow_requests: Array<Record<string, unknown>>;
  workflow_artifacts: Array<Record<string, unknown>>;
}): boolean {
  return (
    rows.workflow_requests.some((request) => Boolean(request.approved_scoper_artifact_id)) ||
    rows.workflow_artifacts.some(
      (artifact) => artifact.artifact_type === "approved_scope" && artifact.status === "approved",
    )
  );
}

function openGapIds(gaps: Array<Record<string, unknown>>): string[] {
  return gaps
    .filter((gap) => ["open", "selected"].includes(String(gap.status ?? "open")))
    .map((gap) => String(gap.research_workflow_evidence_gap_id ?? ""))
    .filter(Boolean)
    .slice(0, 25);
}

// The authoritative scoper output still waiting on human review. The parent
// pointer, not artifact created_at, identifies what the approval gate may
// consume: older drafts remain on the request when a newer output re-parks the
// same review state. Only review-parked run_scoper requests qualify, so an
// attachment on another request type cannot shadow a genuine pending scope.
export function pendingScoperArtifact(
  rows: Pick<CompanyDetailResponse, "workflow_requests" | "workflow_artifacts">,
): Record<string, unknown> | null {
  const currentArtifactByRequest = new Map(
    rows.workflow_requests
      .filter(
        (request) =>
          request.request_type === "run_scoper" &&
          request.status === "scoper_review_required" &&
          request.workflow_stage === "scoper_review" &&
          Boolean(request.current_artifact_id),
      )
      .map((request) => [
        String(request.research_workflow_request_id ?? ""),
        String(request.current_artifact_id ?? ""),
      ]),
  );
  const pending = rows.workflow_artifacts
    .filter(
      (artifact) => {
        const requestId = String(artifact.research_workflow_request_id ?? "");
        return (
          artifact.artifact_type === "scoper_output" &&
          ["review_required", "draft"].includes(String(artifact.status ?? "")) &&
          currentArtifactByRequest.get(requestId) ===
            String(artifact.research_workflow_artifact_id ?? "")
        );
      },
    )
    .sort((a, b) => String(b.created_at ?? "").localeCompare(String(a.created_at ?? "")));
  return pending[0] ?? null;
}

// Scope review (Wave 2.5): the approval gate that unblocks the five
// approved-scope-dependent actions. Edit the markdown, approve, done.
export function ScopeReviewPanel({
  ticker,
  artifact,
}: {
  ticker: string;
  artifact: Record<string, unknown>;
}) {
  const canApprove = useCan("workflows.approve");
  const queryClient = useQueryClient();
  const artifactId = String(artifact.research_workflow_artifact_id ?? "");
  const requestId = String(artifact.research_workflow_request_id ?? "");
  const original = String(artifact.content_markdown ?? "");
  const [markdown, setMarkdown] = useState(original);
  const [notes, setNotes] = useState("");
  const [outcome, setOutcome] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  // A newer scoper version replaces the editor content; unsent edits to a
  // superseded version are stale by definition.
  useEffect(() => {
    setMarkdown(String(artifact.content_markdown ?? ""));
    setNotes("");
    setOutcome(null);
    setProblem(null);
  }, [artifactId]); // eslint-disable-line react-hooks/exhaustive-deps
  const approve = useMutation({
    mutationFn: () =>
      // content_markdown ONLY when actually amended: an untouched approve
      // must reach the server's plain-approve path (and its double-approve
      // 409 guard), not the always-amend path.
      approveWorkflowArtifact(requestId, artifactId, {
        ...(markdown !== original ? { content_markdown: markdown } : {}),
        ...(notes.trim() ? { review_notes: notes.trim() } : {}),
      }),
    onSuccess: (result) => {
      setProblem(null);
      setOutcome(
        `Scope approved — approved_scope v${result.workflow_artifact.artifact_version} now gates IC actions.`,
      );
      void queryClient.invalidateQueries({ queryKey: ["company", ticker] });
    },
    onError: (error) => {
      setOutcome(null);
      if (error instanceof ApiError && error.status === 409) {
        setProblem(`Approval conflict: ${error.message}`);
        void queryClient.invalidateQueries({ queryKey: ["company", ticker] });
      } else if (error instanceof ApiError && error.status === 403) {
        setProblem("Approving a scope requires the workflows.approve capability.");
      } else {
        setProblem((error as Error).message);
      }
    },
  });
  return (
    <div className="scope-review">
      <div className="muted-line">
        <b>Scope review</b> — {String(artifact.title ?? "Scoper Output")} (v
        {String(artifact.artifact_version ?? "?")}) awaits approval. Amend the markdown if
        needed, then approve to unblock the IC actions.
      </div>
      <textarea
        aria-label="Scope markdown"
        rows={10}
        value={markdown}
        onChange={(event) => setMarkdown(event.target.value)}
        disabled={!canApprove || approve.isPending}
      />
      <div className="inline-editor">
        <input
          aria-label="Review notes"
          placeholder="Review notes (optional)"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          disabled={!canApprove || approve.isPending}
        />
        {canApprove ? (
          <button
            className="btn small primary"
            type="button"
            disabled={approve.isPending}
            onClick={() => approve.mutate()}
          >
            {approve.isPending ? "Approving…" : "Approve scope"}
          </button>
        ) : (
          <span className="muted-line">Approving a scope requires the workflows.approve capability.</span>
        )}
      </div>
      {outcome ? <div className="muted-line">{outcome}</div> : null}
      {problem ? <div className="empty left">{problem}</div> : null}
    </div>
  );
}

export function WorkflowActionsPanel() {
  const canRun = useCan("workflows.run");
  const queryClient = useQueryClient();
  // Deep-link support: the home-inbox "scoper output published" card lands
  // here as /app/operations?ticker=MU with the company preloaded, so the
  // Scope review panel is one click from the notification.
  const [searchParams] = useSearchParams();
  const initialTicker = (searchParams.get("ticker") ?? "").trim().toUpperCase();
  const [tickerInput, setTickerInput] = useState(initialTicker);
  const [ticker, setTicker] = useState(initialTicker);
  const [modelKey, setModelKey] = useState<string | null>(null);
  // Two-click confirmation for anything that spends or launches acquisition:
  // the first click arms the button, the second fires.
  const [armed, setArmed] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const catalog = useQuery({
    queryKey: ["workflow-actions"],
    queryFn: fetchWorkflowActionCatalog,
    enabled: canRun,
  });
  const company = useQuery({
    queryKey: ["company", ticker],
    queryFn: () => fetchCompany(ticker),
    enabled: canRun && ticker !== "",
    retry: false,
  });

  const dispatch = useMutation({
    mutationFn: (action: WorkflowActionDefinition) => {
      const body: Parameters<typeof createCompanyWorkflowRequest>[1] = {
        request_type: action.type,
        depth: action.depth,
      };
      const catalogData = catalog.data;
      if (catalogData && catalogData.model_selectable_types.includes(action.type)) {
        body.scoper_model_key = modelKey ?? catalogData.default_scoper_model;
      }
      if (["fill_evidence_gaps", "rerun_with_new_evidence"].includes(action.type)) {
        body.selected_gap_ids = openGapIds(company.data?.evidence_gaps ?? []);
      }
      return createCompanyWorkflowRequest(ticker, body);
    },
    onSuccess: (created) => {
      const request = created.workflow_request;
      setFailure(null);
      setResult(
        created.message ??
          `Queued ${request.request_type} — request ${request.research_workflow_request_id} (${request.status}).`,
      );
      void queryClient.invalidateQueries({ queryKey: ["company", ticker] });
    },
    onError: (error) => {
      setResult(null);
      if (error instanceof ApiError && error.status === 409) {
        setFailure(`Already running: ${error.message}`);
      } else if (error instanceof ApiError && error.status === 403) {
        setFailure("Triggering workflow actions requires the workflows.run capability.");
      } else {
        setFailure((error as Error).message);
      }
    },
  });

  if (!canRun) {
    return (
      <section className="surface-section span-two">
        <div className="section-head"><h2>Workflow actions</h2></div>
        <div className="empty left">Triggering workflow actions requires the workflows.run capability.</div>
      </section>
    );
  }

  const actions = catalog.data?.actions ?? [];
  const groups = [...new Set(actions.map((action) => action.group))];
  const approved = company.data ? hasApprovedScope(company.data) : false;
  const pendingScope = company.data ? pendingScoperArtifact(company.data) : null;
  const gapCount = company.data ? openGapIds(company.data.evidence_gaps).length : 0;
  const companyReady = company.isSuccess;

  function disabledReason(action: WorkflowActionDefinition): string | null {
    if (!companyReady) return "Load a company first.";
    if (action.needs === "approved_scope" && !approved) return "Approve the scoper output first (Scope review panel).";
    if (action.needs === "evidence_gaps" && gapCount === 0) return "No open evidence gaps yet.";
    return null;
  }

  function fire(action: WorkflowActionDefinition) {
    setResult(null);
    setFailure(null);
    if (action.confirm && armed !== action.type) {
      setArmed(action.type);
      return;
    }
    setArmed(null);
    dispatch.mutate(action);
  }

  return (
    <section className="surface-section span-two">
      <div className="section-head"><h2>Workflow actions</h2><span>{actions.length}</span></div>
      <div className="muted-line">
        Manual triggers for the company research workflow. Requests queue in the database; the
        worker fleet claims and runs them.
      </div>
      <form
        className="inline-editor"
        onSubmit={(event) => {
          event.preventDefault();
          const next = tickerInput.trim().toUpperCase();
          if (next) {
            setTicker(next);
            setArmed(null);
            setResult(null);
            setFailure(null);
          }
        }}
      >
        <input
          aria-label="Company ticker"
          placeholder="Ticker (e.g. MU)"
          value={tickerInput}
          onChange={(event) => setTickerInput(event.target.value)}
        />
        <button className="btn small" type="submit">Load company</button>
        <select
          aria-label="Scoper model"
          value={modelKey ?? catalog.data?.default_scoper_model ?? ""}
          onChange={(event) => setModelKey(event.target.value)}
        >
          {(catalog.data?.scoper_models ?? []).map((model) => (
            <option key={model.key} value={model.key}>
              {model.label} · {model.reasoning_effort}
            </option>
          ))}
        </select>
      </form>
      {catalog.isError ? <div className="empty left">Action catalog failed — {(catalog.error as Error).message}</div> : null}
      {company.isLoading && ticker ? <div className="empty left">Loading {ticker}…</div> : null}
      {company.isError ? (
        <div className="empty left">
          {company.error instanceof ApiError && company.error.status === 404
            ? `Company not found: ${ticker}`
            : `Company lookup failed — ${(company.error as Error).message}`}
        </div>
      ) : null}
      {company.data ? (
        <div className="muted-line">
          {String(company.data.company.company_name ?? ticker)} · scope {approved ? "approved" : "not approved"} · {gapCount} open gap{gapCount === 1 ? "" : "s"}
        </div>
      ) : null}
      {company.data && pendingScope ? (
        <ScopeReviewPanel key={String(pendingScope.research_workflow_artifact_id ?? "")} ticker={ticker} artifact={pendingScope} />
      ) : null}
      {company.data && !pendingScope && !approved ? (
        <div className="muted-line">
          No scoper output awaits review — run the scoper first, then approve its output here.
        </div>
      ) : null}
      {groups.map((group) => (
        <div key={group}>
          <div className="muted-line"><b>{group}</b></div>
          <div className="filter-row">
            {actions
              .filter((action) => action.group === group)
              .map((action) => {
                const reason = disabledReason(action);
                return (
                  <button
                    key={action.type}
                    className={`btn small${armed === action.type ? " primary" : ""}`}
                    type="button"
                    title={reason ?? action.description}
                    disabled={reason !== null || dispatch.isPending}
                    onClick={() => fire(action)}
                  >
                    {armed === action.type ? `Confirm ${action.label}?` : action.label}
                  </button>
                );
              })}
          </div>
        </div>
      ))}
      {result ? <div className="muted-line">{result}</div> : null}
      {failure ? <div className="empty left">{failure}</div> : null}
    </section>
  );
}

function OperationsSnapshot() {
  const operations = useQuery({ queryKey: ["operations"], queryFn: fetchOperations, refetchInterval: 60_000 });
  if (operations.isLoading) return <div className="empty">Loading operations...</div>;
  if (operations.isError || !operations.data) return <div className="error-state">{(operations.error as Error)?.message ?? "Operations unavailable."}</div>;
  const data = operations.data;
  return <>
    <div className="filter-row"><button className="btn" type="button" onClick={() => operations.refetch()} disabled={operations.isFetching}>{operations.isFetching ? "Refreshing..." : "Refresh"}</button></div>
    <div className="metric-strip"><div><b>{data.summary.running_count ?? 0}</b><span>Running</span></div><div><b>{data.summary.queued_count ?? 0}</b><span>Queued</span></div><div><b>{data.summary.failed_count ?? 0}</b><span>Failed 24h</span></div><div><b>{data.summary.attention_count ?? 0}</b><span>Attention</span></div></div>
    <div className="operations-grid">
      <section className="surface-section"><div className="section-head"><h2>Portal workers</h2><span>{data.workers.length}</span></div><div className="record-list">{data.workers.map((worker) => <div className="record-row" key={worker.portal_name}><div><b>{worker.portal_name}</b><div className="muted-line">{worker.worker_id || "No worker"} · checked {ago(worker.checked_at)}</div></div><span className={`chip ${tone(worker.status)}`}>{worker.status}</span></div>)}</div>{data.workers.length === 0 ? <div className="empty left">No worker health has been reported.</div> : null}</section>
      <section className="surface-section"><div className="section-head"><h2>SQS queues</h2><span>{data.queues.sqs.length}</span></div><div className="table-scroll"><table className="data surface-table"><thead><tr><th>Queue</th><th className="num">Visible</th><th className="num">In flight</th><th className="num">Delayed</th></tr></thead><tbody>{data.queues.sqs.map((queue) => <tr key={queue.id}><td>{queue.name}</td><td className="num">{queue.visible ?? 0}</td><td className="num">{queue.in_flight ?? 0}</td><td className="num">{queue.delayed ?? 0}</td></tr>)}</tbody></table></div></section>
      <section className="surface-section span-two"><div className="section-head"><h2>Active runs</h2><span>{data.runs.active.length}</span></div><div className="table-scroll"><table className="data surface-table"><thead><tr><th>Kind</th><th>Run</th><th>Status</th><th>Priority</th><th>Age</th></tr></thead><tbody>{data.runs.active.map((run) => <tr key={`${run.kind}-${run.id}`}><td>{run.kind.replaceAll("_", " ")}</td><td>{run.label}</td><td><span className={`chip ${tone(run.status)}`}>{run.status}</span></td><td>{run.priority || "-"}</td><td>{duration(run.duration_seconds)}</td></tr>)}</tbody></table></div>{data.runs.active.length === 0 ? <div className="empty">No active runs.</div> : null}</section>
      <section className="surface-section"><div className="section-head"><h2>Ingestion</h2><span>{data.ingestion.length} pipelines</span></div><div className="record-list">{data.ingestion.map((group) => <div className="record-row" key={group.id}><div><b>{group.name}</b><div className="muted-line">{group.counts.map((count) => `${String(count.status)} ${String(count.count)}`).join(" · ")}</div></div><span>{group.total}</span></div>)}</div></section>
      <section className="surface-section"><div className="section-head"><h2>Alerts</h2><span>{data.alerts.length}</span></div><div className="record-list">{data.alerts.map((alert, index) => <div className="record-row align-start" key={`${alert.kind}-${alert.label}-${index}`}><div><b>{alert.label}</b><div className="muted-line">{alert.error || alert.kind} · {ago(alert.at)}</div></div><span className={`chip ${tone(alert.status)}`}>{alert.status}</span></div>)}</div>{data.alerts.length === 0 ? <div className="empty left">No current alerts.</div> : null}</section>
    </div>
    <div className="snapshot-time">Snapshot {ago(data.generated_at)} · refreshes every minute</div>
  </>;
}

export default function Operations() {
  const canReadOperations = useCan("operations.read");
  // The actions panel renders independently of the snapshot: a workflows.run
  // user gets the company research triggers without receiving operations data.
  return <>
    <div className="pagehead operations-head"><div>
      <h1>{canReadOperations ? "Operations" : "Research actions"}</h1>
      <div className="sub">
        {canReadOperations
          ? "Workers, queues, runs, ingestion, alerts, and company research actions."
          : "Launch and review company research workflows without exposing infrastructure operations."}
      </div>
    </div></div>
    <div className="operations-grid"><WorkflowActionsPanel /></div>
    {canReadOperations ? <OperationsSnapshot /> : null}
  </>;
}
